'use client';
import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import StatusBadge from '@/components/ui/StatusBadge';
import { createClient } from '@/lib/supabase/client';

interface BookingItem {
  id: string;
  member: string;
  sport: string;
  court: string;
  time: string;
  duration: string;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed' | 'no-show';
}

function getTodayStr(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export default function UpcomingBookings() {
  const [bookings, setBookings] = useState<BookingItem[]>([]);

  useEffect(() => {
    async function load() {
      try {
        const supabase = createClient();
        const today = getTodayStr();
        const { data } = await supabase
          .from('bookings')
          .select('id, member_name, sport, court, start_time, duration, status')
          .eq('date', today)
          .in('status', ['confirmed', 'pending'])
          .order('start_time', { ascending: true })
          .limit(6);

        setBookings(
          (data ?? []).map((r) => ({
            id: r.id,
            member: r.member_name ?? '',
            sport: r.sport ?? '',
            court: r.court ?? '',
            time: r.start_time ?? '',
            duration: r.duration ?? '',
            status: r.status ?? 'pending',
          }))
        );
      } catch {
        setBookings([]);
      }
    }
    load();
  }, []);

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[15px] font-semibold text-foreground">Upcoming Bookings</h3>
          <p className="text-[12px] text-muted-foreground">Today's remaining schedule</p>
        </div>
        <Link href="/slot-court-bookings" className="flex items-center gap-1 text-[12px] text-primary font-medium hover:underline">
          Manage <ArrowRight size={12} />
        </Link>
      </div>
      {bookings.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-[13px] text-muted-foreground">
          No upcoming bookings today
        </div>
      ) : (
        <ul className="space-y-2">
          {bookings.map((b) => (
            <li key={b.id} className="flex items-center gap-3 py-1.5 hover:bg-muted/50 rounded-lg px-1 transition-colors">
              <div className="flex-shrink-0 w-14 text-right">
                <span className="text-[12px] font-mono font-semibold text-foreground">{b.time}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-foreground truncate">{b.member}</p>
                <p className="text-[11px] text-muted-foreground">{b.sport} · {b.court} · {b.duration}</p>
              </div>
              <StatusBadge variant={b.status} size="sm" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}